import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import { useRoleCatalogQuery } from '../api/use-role-catalog-query'
import { PERMISSION_ACTIONS, type RoleModule } from '../types/role'

interface EffectivePermissionsPanelProps {
  modules: RoleModule[]
  /** Heading of the panel; one role's matrix in My Access names the role instead (UC-084.9). */
  title?: string
}

/**
 * Read-only view of what the selected roles add up to (TH-459 mockup step 4).
 * Advisory: the server is the one that rules on SoD over this union.
 *
 * Rows follow the catalog, like the roles matrix: a key the catalog does not
 * list (a legacy-shaped preset still carrying pre-split keys, CU-91 deviation 8)
 * is not shown. One fixed column per action keeps the grants aligned.
 */
export function EffectivePermissionsPanel({ modules, title }: EffectivePermissionsPanelProps) {
  const { t } = useTranslation()
  const { data: catalog = [] } = useRoleCatalogQuery()
  const heading = title ?? t('effectivePermissions.title')
  const granted = new Map(modules.map((module) => [module.Key, module]))
  const rows = catalog.flatMap((catalogRow) => {
    const module = granted.get(catalogRow.Key)
    return module ? [{ key: catalogRow.Key, label: catalogRow.Label, module }] : []
  })

  return (
    <section aria-label={heading} className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold">{heading}</h3>
      {modules.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('effectivePermissions.empty')}</p>
      ) : (
        <table className="w-full table-fixed text-sm">
          <thead>
            <tr className="border-b text-xs text-muted-foreground">
              <th scope="col" className="py-1 pr-2 text-left font-medium">
                <span className="sr-only">{t('effectivePermissions.module')}</span>
              </th>
              {PERMISSION_ACTIONS.map((action) => (
                <th key={action} scope="col" className="w-14 py-1 text-center font-medium">
                  {t(`roleMatrix.actions.${action}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ key, label, module }) => (
              <tr key={key} className="border-b last:border-0">
                <th scope="row" className="py-1.5 pr-2 text-left font-normal">{label}</th>
                {PERMISSION_ACTIONS.map((action) => (
                  <td key={action} className="py-1.5 text-center">
                    {module[action] && (
                      <Check
                        role="img"
                        aria-label={t(`roleMatrix.actions.${action}`)}
                        className="inline h-4 w-4 text-primary-strong"
                      />
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}
